import { useMemo } from 'react';

import classnames from 'classnames';
import filter from 'lodash/filter';
import indexOf from 'lodash/indexOf';
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';
import { connect } from 'react-redux';

import CabinPrice from '@/components/CabinCard/CabinPrice';
import { PriceFootnote } from '@/components/elements/PriceFootnote';
import Uplift from '@/components/Uplift';
import { selectCabins, selectPriceType, selectSailors, selectSortType } from '@/ducks/filters/selectors';
import useStrikethrough from '@/ducks/programs/strikethrough/useStrikethrough';
import { selectSailingData } from '@/ducks/voyageInfo/sailing/selectors';
import { FormattedMessage } from '@/helpers/formatted-message';
import { getColorFromSCSS } from '@/helpers/styles/getColorFromSCSS';
import { checkForValueType } from '@/helpers/util';
import { formatDate } from '@/helpers/util/dateUtil';

import ItineraryFeatureCard from './ItineraryFeatureCard';

import './ItineraryFeatureCards.scss';

export const LIGHT_SKELETON_PROPS = { height: '48px', width: '92px' };
export const DARK_SKELETON_PROPS = {
  ...LIGHT_SKELETON_PROPS,
  baseColor: getColorFromSCSS('brass-dark'),
  highlightColor: getColorFromSCSS('brass-light'),
};

const ItineraryFeatureCards = ({ cabinCategoriesData = {}, cabins, onClick, priceType, sailingData, sailorCount }) => {
  const getStrikethroughForPackage = useStrikethrough();

  const cssClasses = classnames({
    ItineraryFeatureCards: true,
  });

  const updatedCabinCategories = useMemo(
    () => orderBy(cabinCategoriesData, ['navigationOrder'], ['asc']),
    [cabinCategoriesData],
  );
  const vipCategories = useMemo(() => filter(updatedCabinCategories, (cabin) => cabin.isVip), [updatedCabinCategories]);
  const normalCategories = useMemo(
    () => filter(updatedCabinCategories, (cabin) => !cabin.isVip),
    [updatedCabinCategories],
  );

  const nonVipCabinClasses = classnames({
    nonVipCabin: true,
  });

  const sailingDataPortsCheck = checkForValueType(sailingData?.ports);
  const sailingDateStartDateCheck = checkForValueType(sailingData?.startDate);

  const onCardClick = (cabin) => () => {
    onClick?.(cabin.name);
  };

  return (
    <>
      <div className="cabinsHeadingSubHeading">
        <h2 className="cabinsHeading">
          <FormattedMessage defaultMessage="Rest your head" id="ItineraryDetail.availableCabinsTitle" />
        </h2>
        <div className="cabinsSubHeading">
          <FormattedMessage
            defaultMessage="Coffee (or cocktails) on a balcony overlooking the waves, or waking up late in your own cocoon, here are the cabins you can choose from&hellip;"
            id="ItineraryDetail.availableCabinsDescription"
          />
        </div>
      </div>
      <div className={cssClasses}>
        {checkForValueType(updatedCabinCategories) &&
          [...updatedCabinCategories].reverse().map((cabin, idx) => {
            const priceDetails = cabin?.lowestSubCategoryPrice?.totalPrice;
            const amount = priceDetails?.amount;
            const lowestSubCategoryCode = cabin.lowestSubCategoryPrice?.subCategoryCode;
            const strikethroughValue = getStrikethroughForPackage(lowestSubCategoryCode);
            return (
              <ItineraryFeatureCard
                alt="ItineraryFeatureCard-img"
                aspectRatio={113 / 201}
                centerText
                imageAlign={idx % 2 === 0 ? 'left' : 'right'}
                imgUrl={cabin.smallThumbnail?.src}
                key={idx}
              >
                <h3 className="cabinName">{cabin.name}</h3>
                <div className="price">
                  {!cabin.isAvailable ? (
                    <span className="">
                      <FormattedMessage defaultMessage="Unavailable" id="CabinCard.CabinCategoryCard.unavailable" />
                    </span>
                  ) : (
                    <>
                      <FormattedMessage defaultMessage="From" id="ItineraryDetail.from" />{' '}
                      <CabinPrice
                        abTestPriceType={priceType}
                        cabins={cabins}
                        currencyCode={priceDetails?.currencyCode}
                        duration={sailingData?.duration}
                        priceType={priceType}
                        sailors={sailorCount}
                        startingPrice={priceDetails}
                        strikethroughValue={strikethroughValue}
                      />{' '}
                      <div>
                        <PriceFootnote priceType={priceType} />
                      </div>
                    </>
                  )}
                </div>
                {sailingDataPortsCheck && sailingDateStartDateCheck && (
                  <Uplift
                    currencyCode={priceDetails?.currencyCode}
                    isHorizontal
                    isLoadUpliftDisabled={idx !== cabinCategoriesData?.length - 1}
                    isTooltipDisabled={false}
                    price={amount}
                    sailing={sailingData}
                    sailors={sailorCount}
                    showOrFromLabel
                    skeletonProps={LIGHT_SKELETON_PROPS}
                  />
                )}
              </ItineraryFeatureCard>
            );
          })}
      </div>

      <div className="cabinCardsDesktop">
        <div className="vipCabinsSection">
          {checkForValueType(vipCategories) &&
            vipCategories?.map((cabin) => {
              const priceDetails = cabin?.lowestSubCategoryPrice?.totalPrice;
              const darkTheme = indexOf(cabin?.tags, 'web-theme-black') >= 0;
              const vipCabinClasses = classnames({
                '-brass': !darkTheme,
                '-darkBrass': darkTheme,
                vipCabin: true,
              });
              const amount = priceDetails?.amount;
              const lowestSubCategoryCode = cabin.lowestSubCategoryPrice?.subCategoryCode;
              const strikethroughValue = getStrikethroughForPackage(lowestSubCategoryCode);
              return (
                // eslint-disable-next-line react/jsx-key
                <div className={vipCabinClasses} onClick={onCardClick(cabin)} onKeyDown={onCardClick(cabin)}>
                  {cabin.smallThumbnail?.src && (
                    <img alt={cabin.smallThumbnail?.alt || ''} aria-hidden src={cabin.smallThumbnail?.src} />
                  )}
                  <div className="vipCabinPriceSection">
                    <h3 className="cabinName">{cabin.name}</h3>
                    <div className="vipCabinPrice">
                      {!cabin.isAvailable ? (
                        <span className="">
                          <FormattedMessage defaultMessage="Unavailable" id="CabinCard.CabinCategoryCard.unavailable" />
                        </span>
                      ) : (
                        <>
                          <FormattedMessage defaultMessage="From" id="ItineraryDetail.from" />{' '}
                          <span className="amount">
                            <CabinPrice
                              abTestPriceType={priceType}
                              cabins={cabins}
                              currencyCode={priceDetails?.currencyCode}
                              duration={sailingData?.duration}
                              priceType={priceType}
                              sailors={sailorCount}
                              startingPrice={priceDetails}
                              strikethroughValue={strikethroughValue}
                            />
                          </span>
                          <div>
                            <PriceFootnote priceType={priceType} />
                          </div>
                          <div>
                            {sailingDataPortsCheck && sailingDateStartDateCheck && (
                              <Uplift
                                currencyCode={priceDetails?.currencyCode}
                                isHorizontal
                                isTooltipDisabled={false}
                                price={amount}
                                sailing={sailingData}
                                sailors={sailorCount}
                                showOrFromLabel
                                skeletonProps={DARK_SKELETON_PROPS}
                              />
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
        <div className="normalCabinsSection">
          {checkForValueType(normalCategories) &&
            normalCategories?.map((cabin) => {
              const priceDetails = cabin?.lowestSubCategoryPrice?.totalPrice;
              const isAvailable = cabin?.isAvailable;
              const amount = priceDetails?.amount;
              const lowestSubCategoryCode = cabin.lowestSubCategoryPrice?.subCategoryCode;
              const strikethroughValue = getStrikethroughForPackage(lowestSubCategoryCode);
              return (
                // eslint-disable-next-line react/jsx-key
                <div className={nonVipCabinClasses} onClick={onCardClick(cabin)} onKeyDown={onCardClick(cabin)}>
                  {cabin.smallThumbnail?.src && (
                    <img alt={cabin.smallThumbnail?.alt || ''} aria-hidden src={cabin.smallThumbnail?.src} />
                  )}
                  <div className="nonVipCabinPriceSection">
                    <h3 className="cabinName">{cabin.name}</h3>
                    <div className="nonVipCabinPrice">
                      {isEmpty(priceDetails) || !isAvailable ? (
                        <span className="">
                          <FormattedMessage defaultMessage="Unavailable" id="CabinCard.CabinCategoryCard.unavailable" />
                        </span>
                      ) : (
                        <>
                          <FormattedMessage defaultMessage="From" id="ItineraryDetail.from" />{' '}
                          <span className="amount">
                            <CabinPrice
                              abTestPriceType={priceType}
                              cabins={cabins}
                              currencyCode={priceDetails?.currencyCode}
                              duration={sailingData?.duration}
                              priceType={priceType}
                              sailors={sailorCount}
                              startingPrice={priceDetails}
                              strikethroughValue={strikethroughValue}
                            />
                          </span>
                          <div>
                            <PriceFootnote priceType={priceType} />
                          </div>
                          <div>
                            {sailingDataPortsCheck && sailingDateStartDateCheck && (
                              <Uplift
                                currencyCode={priceDetails?.currencyCode}
                                isHorizontal
                                isTooltipDisabled={false}
                                price={amount}
                                sailing={sailingData}
                                sailors={sailorCount}
                                showOrFromLabel
                                skeletonProps={LIGHT_SKELETON_PROPS}
                              />
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      <div className="cabinsHeadingSubHeading">
        <div className="cabinsFooter" suppressHydrationWarning>
          <FormattedMessage
            defaultMessage="Prices based on {date} sailing with {noOfSailors} sailors sharing a cabin, {taxText}"
            id="PriceBasis.label"
            values={{
              date: formatDate(sailingData?.embarkDate, 'MMM dd yyy'),
              noOfSailors: sailorCount,
              taxText: (
                <FormattedMessage defaultMessage="Incl. taxes & fees" id="MainNav.Summary.tax.including.mobile" />
              ),
            }}
          />
        </div>
      </div>
    </>
  );
};

// TODO: Can be replaced by useSelector hook just now!
function mapStateToProps(state) {
  return {
    cabins: selectCabins(state),
    priceType: selectPriceType(state),
    sailingData: selectSailingData(state),
    sailorCount: selectSailors(state),
    sortType: selectSortType(state),
  };
}

export default connect(mapStateToProps)(ItineraryFeatureCards);
